import { Component } from '@theme/component';

import { ThemeEvents } from '@theme/events';

import { StandardEvents } from '@shopify/events';


export class MediaGallery extends Component {
  #controller = new AbortController();

  #displayObserver = null;

  #colorChangeInProgress = false;


  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    const target = this.closest(
      '.shopify-section, dialog'
    );

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    this.#startDisplayFix();
  }


  disconnectedCallback() {
    this.#displayObserver?.disconnect();

    this.#displayObserver = null;

    this.#controller.abort();

    super.disconnectedCallback();
  }


  /*
   * ---------------------------------------------------------
   * DISPLAY FIX
   * ---------------------------------------------------------
   */

  #fixActiveSlideDisplay = () => {
    if (!this.isConnected) return;

    const activeSlide = this.querySelector(
      'slideshow-slide[aria-hidden="false"]'
    );

    if (!activeSlide) return;


    if (activeSlide.style.display === 'none') {
      activeSlide.style.removeProperty(
        'display'
      );
    }


    activeSlide.style.setProperty(
      '--slide-width',
      '100%',
      'important'
    );

    activeSlide.style.setProperty(
      'width',
      '100%',
      'important'
    );

    activeSlide.style.setProperty(
      'flex',
      '0 0 100%',
      'important'
    );


    if (
      getComputedStyle(
        activeSlide
      ).display === 'none'
    ) {
      activeSlide.style.setProperty(
        'display',
        'flex',
        'important'
      );
    }
  };


  #startDisplayFix = () => {
    this.#fixActiveSlideDisplay();

    requestAnimationFrame(() => {
      this.#fixActiveSlideDisplay();

      requestAnimationFrame(() => {
        this.#fixActiveSlideDisplay();
      });
    });


    this.#displayObserver?.disconnect();


    this.#displayObserver =
      new MutationObserver(() => {
        requestAnimationFrame(() => {
          this.#fixActiveSlideDisplay();
        });
      });


    this.#displayObserver.observe(
      this,
      {
        subtree: true,
        attributes: true,
        attributeFilter: [
          'style',
          'aria-hidden',
          'class',
          'hidden',
        ],
      }
    );
  };


  /*
   * ---------------------------------------------------------
   * OPTION HELPERS
   * ---------------------------------------------------------
   */

  #getSelectedOptionElement(
    variantPicker,
    optionValueId
  ) {
    if (
      !variantPicker ||
      !optionValueId
    ) {
      return null;
    }


    const options =
      variantPicker.querySelectorAll(
        '[data-option-value-id]'
      );


    return Array.from(
      options
    ).find(
      (element) =>
        String(
          element.dataset.optionValueId
        ) ===
        String(optionValueId)
    );
  }


  #isColorOption(element) {
    if (!element) return false;


    const optionName = (
      element.dataset.optionName ||
      ''
    )
      .trim()
      .toLowerCase();


    return (
      optionName === 'color' ||
      optionName === 'colors' ||
      optionName === 'colour' ||
      optionName === 'colours'
    );
  }


  /*
   * ---------------------------------------------------------
   * COLOR CHANGE
   * ---------------------------------------------------------
   */

  #handleColorChange = (
    variantPicker,
    selectedOption
  ) => {
    if (
      this.#colorChangeInProgress
    ) {
      return;
    }


    const variantId =
      selectedOption?.dataset?.variantId;


    if (!variantId) {
      console.warn(
        '[media-gallery] Color option has no variant ID.'
      );

      return;
    }


    this.#colorChangeInProgress = true;


    /*
     * Use Shopify's product URL.
     */

    let productUrl =
      variantPicker.dataset.productUrl;


    if (!productUrl) {
      productUrl =
        window.location.pathname;
    }


    try {
      const url = new URL(
        productUrl,
        window.location.origin
      );


      /*
       * Selected Color/variant.
       */

      url.searchParams.set(
        'variant',
        variantId
      );


      /*
       * Preserve any existing relevant
       * query parameters.
       */


      /*
       * Navigate to the selected variant.
       *
       * Shopify will render the correct
       * variant gallery server-side.
       */

      window.location.href =
        url.toString();

    } catch (error) {
      console.error(
        '[media-gallery] Could not navigate to selected Color:',
        error
      );

      this.#colorChangeInProgress = false;
    }
  };


  /*
   * ---------------------------------------------------------
   * PRODUCT SELECT
   * ---------------------------------------------------------
   */

  #handleProductSelect = (
    event
  ) => {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }


    /*
     * Ignore product cards.
     */

    if (
      event.target.closest(
        'product-card'
      )
    ) {
      return;
    }


    const eventData =
      event.detail ?? {};


    const selectionDetail =
      eventData.detail ?? {};


    const optionValueId =
      selectionDetail.optionValueId;


    if (!optionValueId) {
      this.#startDisplayFix();
      return;
    }


    /*
     * Find variant picker.
     */

    const section =
      this.closest(
        '.shopify-section, dialog'
      );


    const variantPicker =
      event.target.closest(
        'variant-picker'
      ) ||
      section?.querySelector(
        'variant-picker'
      );


    if (!variantPicker) {
      this.#startDisplayFix();
      return;
    }


    /*
     * Find selected option.
     */

    const optionElement =
      this.#getSelectedOptionElement(
        variantPicker,
        optionValueId
      );


    if (!optionElement) {
      this.#startDisplayFix();
      return;
    }


    /*
     * -------------------------------------------------------
     * SIZE
     * -------------------------------------------------------
     *
     * Size change:
     *
     * DO NOTHING to gallery.
     */

    if (
      !this.#isColorOption(
        optionElement
      )
    ) {
      requestAnimationFrame(() => {
        this.#fixActiveSlideDisplay();
      });

      return;
    }


    /*
     * -------------------------------------------------------
     * COLOR
     * -------------------------------------------------------
     *
     * Automatically navigate to the selected
     * variant URL.
     *
     * Shopify server will then render the
     * correct Color gallery.
     */

    this.#handleColorChange(
      variantPicker,
      optionElement
    );
  };


  /*
   * ---------------------------------------------------------
   * ZOOM
   * ---------------------------------------------------------
   */

  #handleZoomMediaSelected = (
    event
  ) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      {
        animate: false,
      }
    );
  };


  zoom(
    index,
    event
  ) {
    this.refs.zoomDialogComponent?.open(
      index,
      event
    );
  }


  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent
        ?.refs
        .media[index];


    if (!zoomDialogMedia) {
      return;
    }


    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }


  get slideshow() {
    return this.refs.slideshow;
  }


  get media() {
    return this.refs.media;
  }


  get presentation() {
    return this.dataset.presentation;
  }
}


if (
  !customElements.get(
    'media-gallery'
  )
) {
  customElements.define(
    'media-gallery',
    MediaGallery
  );
}